import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  // Session timeout: 24 hours of inactivity
  const SESSION_TIMEOUT = 24 * 60 * 60 * 1000;

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Update activity timestamp on auth events
        if (session) {
          setLastActivity(Date.now());
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session) {
        setLastActivity(Date.now());
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Activity monitoring and session timeout
  useEffect(() => {
    if (!session) return;

    const updateActivity = () => setLastActivity(Date.now());
    
    // Monitor user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    // Check for session timeout every minute
    const timeoutCheck = setInterval(() => {
      if (Date.now() - lastActivity > SESSION_TIMEOUT) {
        signOut();
      }
    }, 60000);

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
      clearInterval(timeoutCheck);
    };
  }, [session, lastActivity]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return {
    user,
    session,
    loading,
    signOut
  };
};